/**
 * HomologationContext (T12) — dev/CNDA-only INSi homologation state.
 *
 * Holds the selected test `scenarioId` and a `captureRequested` toggle, set via
 * the HomologationBanner and read at the `searchInsByTraits` call site to inject
 * the test headers (tech_design_spec §9). The values are NEVER placed in the
 * GraphQL body — only in HTTP headers, and only when the build flag is on.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';

import {
  INSI_TEST_SCENARIO_HEADER,
  INSI_CAPTURE_HEADER,
} from 'services/insi/constants';
import { INSI_HOMOLOGATION_ENABLED } from 'config';

export interface HomologationState {
  scenarioId: string;
  setScenarioId: (id: string) => void;
  captureRequested: boolean;
  setCaptureRequested: (value: boolean) => void;
}

// Empty = homologation "off": no scenario header is sent. The operator opts in
// by picking a scenario from the banner; buildHomologationHeaders returns {} for
// an empty id, so a search runs normally even while the homologation build is on.
const DEFAULT_SCENARIO_ID = '';

const defaultState: HomologationState = {
  scenarioId: DEFAULT_SCENARIO_ID,
  setScenarioId: () => {},
  captureRequested: false,
  setCaptureRequested: () => {},
};

const HomologationContext = createContext<HomologationState>(defaultState);

export const HomologationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [scenarioId, setScenarioId] = useState<string>(DEFAULT_SCENARIO_ID);
  const [captureRequested, setCaptureRequested] = useState<boolean>(false);

  const value = useMemo(
    () => ({
      scenarioId,
      setScenarioId,
      captureRequested,
      setCaptureRequested,
    }),
    [scenarioId, captureRequested]
  );

  return (
    <HomologationContext.Provider value={value}>
      {children}
    </HomologationContext.Provider>
  );
};

export const useHomologation = (): HomologationState =>
  useContext(HomologationContext);

/**
 * Build the INSi homologation HTTP headers for the search call. Returns `{}`
 * unless the build flag is on AND a scenario is selected — so production builds
 * (flag off) never attach anything.
 */
export const buildHomologationHeaders = (state: {
  scenarioId?: string;
  captureRequested?: boolean;
}): Record<string, string> => {
  if (!INSI_HOMOLOGATION_ENABLED || !state.scenarioId) return {};
  return {
    [INSI_TEST_SCENARIO_HEADER]: state.scenarioId,
    ...(state.captureRequested ? { [INSI_CAPTURE_HEADER]: 'true' } : {}),
  };
};

export default HomologationContext;
